/**
 * Record type identity strip — the one way a surface says "this is a Call".
 *
 * Every record card, list row, and detail header in the portal is the same
 * rounded box of text. Without a fixed identity marker a reader has to infer
 * the type from its contents, which is exactly the work that made LeadLoop feel
 * like unrelated pages bolted together: a Lead card and a Form Entry card were
 * distinguishable only by which fields happened to be filled in.
 *
 * The strip is compact and fixed:
 *   - It carries the theme's emphasis pair, so Cascade renders blue and One Up
 *     renders maroon with no per-portal branching. There is no per-type palette:
 *     eight brand colours would compete with the status badges, which are the
 *     one thing on a card that SHOULD carry meaning through colour.
 *   - It is ~18px tall and never wraps, so adding it to a card costs one line
 *     of height rather than a header block.
 *   - The icon is small and sits beside the label rather than replacing it,
 *     because an icon alone is a guess for anyone new to the portal.
 */

import type { LucideIcon } from 'lucide-react';
import type { RecordTypeDefinition as RegistryRecordTypeDefinition } from '@command-center/data-sdk';
import {
  Bot,
  Building2,
  ClipboardList,
  CreditCard,
  FileText,
  FolderKanban,
  Globe2,
  ListChecks,
  Mail,
  PhoneCall,
  Target,
  Timer,
  User,
} from 'lucide-react';
import { cn } from '../lib/utils';

export type RecordTypeId =
  | 'lead'
  | 'form_entry'
  | 'call'
  | 'contact'
  | 'organization'
  | 'form'
  | 'agent'
  | 'email'
  | 'payment'
  | 'time_entry'
  | 'task'
  | 'project'
  | 'website';

export interface RecordTypeDefinition {
  label: string;
  icon: LucideIcon;
}

export type RegisteredRecordTypeDefinition = Pick<
  RegistryRecordTypeDefinition,
  'key' | 'singularLabel' | 'pluralLabel' | 'icon'
>;

/**
 * The registry. A record type exists here or it does not exist — this is what
 * stops "Form Entry", "Form entry", and "Submission" from being three names for
 * one thing across four files.
 */
export const RECORD_TYPES: Record<RecordTypeId, RecordTypeDefinition> = {
  lead: { label: 'Lead', icon: Target },
  form_entry: { label: 'Form Entry', icon: ClipboardList },
  call: { label: 'Call', icon: PhoneCall },
  contact: { label: 'Contact', icon: User },
  organization: { label: 'Organization', icon: Building2 },
  form: { label: 'Form', icon: FileText },
  agent: { label: 'Agent', icon: Bot },
  email: { label: 'Email', icon: Mail },
  payment: { label: 'Payment', icon: CreditCard },
  time_entry: { label: 'Time Entry', icon: Timer },
  task: { label: 'Task', icon: ListChecks },
  project: { label: 'Project', icon: FolderKanban },
  website: { label: 'Website', icon: Globe2 },
};

export function recordTypeLabel(type: RecordTypeId): string {
  return RECORD_TYPES[type].label;
}

/**
 * The strip itself.
 *
 * `trailing` is for the one fact that belongs beside the type rather than in
 * the card body — an Archived marker, a timestamp. It stays optional so the
 * default is just the type.
 */
export function RecordTypeStrip({
  type,
  trailing,
  className,
}: {
  type: RecordTypeId;
  trailing?: React.ReactNode;
  className?: string;
}) {
  const { label, icon: Icon } = RECORD_TYPES[type];
  return (
    <div
      className={cn(
        'flex h-[18px] shrink-0 items-center gap-1.5 bg-emphasis px-2.5 text-emphasis-foreground',
        className,
      )}
    >
      <Icon className="h-[11px] w-[11px] shrink-0" aria-hidden />
      <span className="truncate text-[10px] font-semibold uppercase leading-none tracking-[0.08em]">
        {label}
      </span>
      {trailing && (
        <span className="ml-auto min-w-0 text-[10px] font-medium leading-none opacity-80">
          {trailing}
        </span>
      )}
    </div>
  );
}

/**
 * Registry-backed identity strip used by app-specific record registries.
 * Apps resolve their own icon name, keeping this shared component independent
 * from any one portal's allowed icon or record catalog.
 */
export function RegisteredRecordTypeStrip({
  definition,
  icon: Icon,
  trailing,
  className,
}: {
  definition: RegisteredRecordTypeDefinition;
  icon: LucideIcon;
  trailing?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex h-[18px] shrink-0 items-center gap-1.5 bg-emphasis px-2.5 text-emphasis-foreground',
        className,
      )}
    >
      <Icon className="h-[11px] w-[11px] shrink-0" aria-hidden />
      <span className="truncate text-[10px] font-semibold uppercase leading-none tracking-[0.08em]">
        {definition.singularLabel}
      </span>
      {trailing && (
        <span className="ml-auto min-w-0 text-[10px] font-medium leading-none opacity-80">
          {trailing}
        </span>
      )}
    </div>
  );
}
